import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { NewsletterWidget } from "./components/NewsletterWidget";
import { BreadcrumbSchema } from "./components/BreadcrumbSchema";
import { DynamicMeta } from "./components/DynamicMeta";

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Scorecard = lazy(() => import("./pages/Scorecard"));
const Simulator = lazy(() => import("./pages/Simulator"));
const Methodology = lazy(() => import("./pages/Methodology"));
const QDayEstimator = lazy(() => import("./pages/QDayEstimator"));
const Sources = lazy(() => import("./pages/Sources"));
const MigrationAdvisor = lazy(() => import("./pages/MigrationAdvisor"));
const UserGuide = lazy(() => import("./pages/UserGuide"));
const WorkshopHandouts = lazy(() => import("./pages/WorkshopHandouts"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Copyright = lazy(() => import("./pages/Copyright"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LicenseCompliance = lazy(() => import("./pages/LicenseCompliance"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <DynamicMeta />
        <BreadcrumbSchema />
        <NewsletterWidget />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/scorecard" element={<Scorecard />} />
            <Route path="/simulator" element={<Simulator />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/qday-estimator" element={<QDayEstimator />} />
            <Route path="/sources" element={<Sources />} />
            <Route path="/migration-advisor" element={<MigrationAdvisor />} />
            <Route path="/user-guide" element={<UserGuide />} />
            <Route path="/workshop-handouts" element={<WorkshopHandouts />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/copyright" element={<Copyright />} />
            <Route path="/license-compliance" element={<LicenseCompliance />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
